import React, { useState, useEffect } from 'react';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Bell, Mail, Slack as SlackIcon, CheckCircle2, XCircle, Send, Loader2 } from 'lucide-react';
import {
  apiGetNotificationSettings, apiUpdateNotificationSettings, apiSendTestNotification
} from '../api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const NotificationSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [channels, setChannels] = useState({ email: false, slack: false, sender_email: null });
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [sendingTest, setSendingTest] = useState(false);

  const reload = async () => {
    try {
      const d = await apiGetNotificationSettings();
      setSettings(d.settings);
      setChannels(d.channels || { email: false, slack: false });
    } catch (e) {
      toast.error('Errore caricamento impostazioni');
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key, channel) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], [channel]: !prev[key][channel] }
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiUpdateNotificationSettings(settings);
      toast.success('Impostazioni salvate');
    } catch {
      toast.error('Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error('Inserisci un email di test'); return; }
    setSendingTest(true);
    try {
      const r = await apiSendTestNotification(testEmail);
      const sent = r.sent || {};
      if (sent.email || sent.slack) {
        const msg = [];
        if (sent.email) msg.push(`Email inviata a ${testEmail}`);
        if (sent.slack) msg.push('Slack notificato');
        toast.success(msg.join(' · '));
      } else {
        toast.error('Nessun canale configurato — aggiungi RESEND_API_KEY e/o SLACK_WEBHOOK_URL al backend');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio test');
    } finally {
      setSendingTest(false);
    }
  };

  if (!settings) {
    return <div className="text-sm text-slate-500 p-6 text-center">Caricamento...</div>;
  }

  const ChannelBadge = ({ active, icon: Icon, label }) => (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-50 text-slate-500 border border-slate-200'}`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
      {active
        ? <CheckCircle2 className="w-3 h-3 text-emerald-600" />
        : <XCircle className="w-3 h-3 text-slate-400" />}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="notification-settings">
      {/* Channel status */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">Stato canali notifiche</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Configura le chiavi nel file <code className="font-mono bg-slate-100 px-1 py-0.5 rounded">/app/backend/.env</code> e riavvia il backend per attivare i canali.
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <ChannelBadge active={channels.email} icon={Mail} label={channels.email ? `Email — ${channels.sender_email || 'attivo'}` : 'Email — RESEND_API_KEY non configurata'} />
          <ChannelBadge active={channels.slack} icon={SlackIcon} label={channels.slack ? 'Slack — attivo' : 'Slack — SLACK_WEBHOOK_URL non configurato'} />
        </div>

        {(channels.email || channels.slack) && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[200px] space-y-1">
              <Label className="text-xs">Email per il test</Label>
              <Input
                data-testid="test-email-input"
                type="email" value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="h-9 text-sm"
                placeholder="email@example.com"
              />
            </div>
            <Button
              data-testid="send-test-btn"
              onClick={sendTest}
              disabled={sendingTest}
              variant="outline"
              className="h-9 gap-2"
            >
              {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Invia test
            </Button>
          </div>
        )}
      </div>

      {/* Event toggles */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-sm font-semibold text-slate-900">Eventi notificati</div>
          <div className="text-xs text-slate-500 mt-0.5">Scegli quali eventi inviare per email e/o Slack.</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase text-slate-600">Evento</th>
              <th className="text-center px-5 py-2 text-xs font-semibold uppercase text-slate-600 w-24">
                <Mail className="w-3.5 h-3.5 inline mr-1" /> Email
              </th>
              <th className="text-center px-5 py-2 text-xs font-semibold uppercase text-slate-600 w-24">
                <SlackIcon className="w-3.5 h-3.5 inline mr-1" /> Slack
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {Object.entries(settings).map(([key, cfg]) => (
              <tr key={key} className="hover:bg-slate-50">
                <td className="px-5 py-3 text-slate-700 font-medium">{cfg.label}</td>
                <td className="px-5 py-3 text-center">
                  <Switch
                    data-testid={`toggle-${key}-email`}
                    checked={cfg.email}
                    onCheckedChange={() => toggle(key, 'email')}
                    disabled={!channels.email}
                  />
                </td>
                <td className="px-5 py-3 text-center">
                  <Switch
                    data-testid={`toggle-${key}-slack`}
                    checked={cfg.slack}
                    onCheckedChange={() => toggle(key, 'slack')}
                    disabled={!channels.slack}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            I toggle dei canali non configurati sono disattivati.
          </div>
          <Button
            data-testid="save-notifications-btn"
            onClick={save}
            disabled={saving}
            className="bg-blue-700 hover:bg-blue-800 h-9 gap-2"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Salva impostazioni
          </Button>
        </div>
      </div>

      {/* Setup guide */}
      <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-5">
        <div className="text-sm font-semibold text-blue-900 mb-2">Come configurare i canali</div>
        <div className="text-xs text-slate-700 space-y-2 leading-relaxed">
          <div>
            <strong>1) Email via Resend.</strong> Registrati su{' '}
            <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-blue-700 underline">resend.com</a>, crea una API Key
            e inserisci <code className="font-mono bg-white px-1 py-0.5 rounded border border-blue-200">RESEND_API_KEY=re_...</code> nel file <code className="font-mono bg-white px-1 py-0.5 rounded border border-blue-200">/app/backend/.env</code>.
            Per la produzione verifica il dominio <code>hc-srl.com</code> e aggiorna <code className="font-mono bg-white px-1 py-0.5 rounded border border-blue-200">SENDER_EMAIL</code>.
          </div>
          <div>
            <strong>2) Slack.</strong> Vai su{' '}
            <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer" className="text-blue-700 underline">api.slack.com/apps</a>, crea una App → Incoming Webhooks → Add to Workspace → copia l'URL e inseriscilo come
            {' '}<code className="font-mono bg-white px-1 py-0.5 rounded border border-blue-200">SLACK_WEBHOOK_URL=https://hooks.slack.com/...</code>.
          </div>
          <div>Dopo aver aggiornato il <code>.env</code>, esegui <code className="font-mono bg-white px-1 py-0.5 rounded border border-blue-200">sudo supervisorctl restart backend</code> e ricarica questa pagina.</div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
